import React from "react";
import {
  FaMedal,
  FaLightbulb,
  FaHandsHelping,
  FaPhoneAlt,
  FaCheckCircle,
  FaPlay,
} from "react-icons/fa";
import { AboutUsData } from "./AboutUsData";
import { motion } from "framer-motion";
import { WhyChooseUs } from "./WhyChooseUs";
import { Link, useLocation } from "react-router-dom";

const AboutUs: React.FC = () => {
     const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace("#", "");
      const element:any = document.getElementById(sectionId);
      const headerOffset = 120; // pixels you want to keep above
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;
      if (element) {
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location]);
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const fade = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
  };
  const values = [
    {
      icon: <FaMedal className="text-5xl text-[#ff1a1a]" />,
      title: "Quality First",
      desc: "We maintain the highest standards to deliver safe, durable, and reliable systems.",
    },
    {
      icon: <FaLightbulb className="text-5xl text-[#ff1a1a]" />,
      title: "Innovation Driven",
      desc: "We constantly improve our designs to meet evolving scientific requirements.",
    },
    {
      icon: <FaHandsHelping className="text-5xl text-[#ff1a1a]" />,
      title: "Strong Partnerships",
      desc: "We work closely with clients, ensuring long-term support and trust.",
    },
  ];

  const team = [
    {
      name: "Dr. Sarah Chen",
      role: "Chief Executive Officer",
      img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    },
    {
      name: "Michael Rodriguez",
      role: "Chief Technology Officer",
      img: "https://images.unsplash.com/photo-1556157382-97eda3ac0f59?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    },
    {
      name: "James Wilson",
      role: "Director of Engineering",
      img: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    },
  ];

  return (
    <div className=" text-gray-300">
      {" "}
      {/* HERO */}
      <section
        className="relative h-[200px] md:h-[300px] flex items-center justify-center bg-cover bg-center mt-[80px]"
        style={{
          backgroundImage: `url(${AboutUsData.heroData.imgUrl})`,
        }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/90 to-gray-900"></div>
        <div className="relative z-10 text-center px-4 max-w-3xl">
          <h1 className="text-white font-extrabold  uppercase text-3xl md:text-4xl lg:text-5xl tracking mb-2">
            {AboutUsData.heroData.title}
            <span className="text-[#ff1a1a]">{AboutUsData.heroData.title2}</span>
          </h1>
          <div className="w-20 h-1 bg-[#ff1a1a] mx-auto rounded-full mb-4"></div>
          <p className="text-white text-base md:text-lg font-workSans font-medium">
            {AboutUsData.heroData.subTitle}
          </p>
        </div>
      </section>
      {/* About PRORES */}
      <motion.section
        className="relative w-full py-20 sm:py-24 bg-white overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ staggerChildren: 0.12 }}
      >
        {/* Background accents */}
        <div className="absolute -left-20 top-32 w-64 h-64 bg-gray-100 rounded-xl skew-x-[-15deg] opacity-60"></div>
        <div className="absolute right-0 bottom-20 w-52 h-52 bg-gray-100 rounded-xl skew-x-[-15deg] opacity-50"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* IMAGE */}
            <motion.div variants={fadeUp} transition={{ duration: 0.6 }}>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <motion.img
                  src={AboutUsData.aboutCompany.imgUrl}
                  alt="About company"
                  className="w-full h-[260px] sm:h-[360px] lg:h-[520px] object-cover"
                  initial={{ scale: 0.96, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                />
              </div>
            </motion.div>

            {/* CONTENT */}
            <div>
              <motion.p
                variants={fadeUp}
                className="text-[#A60000] text-xs sm:text-sm font-semibold tracking-widest uppercase"
              >
                {AboutUsData.aboutCompany.title}
              </motion.p>

              <motion.h2
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-gray-900"
              >
                {AboutUsData.aboutCompany.subTitleOne}{" "}
                <span className="text-[#A60000]">
                  {AboutUsData.aboutCompany.subTitleTwo}
                </span>
                <br />
                {AboutUsData.aboutCompany.subTitleThree}
              </motion.h2>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-gray-600 text-base sm:text-lg leading-relaxed max-w-xl"
              >
                {AboutUsData.aboutCompany.description}
              </motion.p>

              {/* FEATURES */}
              <motion.div
                variants={fadeUp}
                className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {AboutUsData.aboutCompany.featureList.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                  >
                    <FaCheckCircle className="text-[#A60000] text-base" />
                    <span className="text-gray-800 text-sm font-medium">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA */}
              <motion.div
                variants={fadeUp}
                className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
              >
                <Link to="/contact-us#contact-form" className="bg-[#A60000] hover:bg-red-700 text-white px-8 py-3 rounded-lg text-sm sm:text-base font-semibold shadow-md transition">
                  {AboutUsData.aboutCompany.ctaPrimary}
                </Link>

                <div className="flex items-center gap-4 sm:border-l sm:border-gray-300 sm:pl-6">
                  <div className="w-12 h-12 rounded-full border-2 border-[#A60000] flex items-center justify-center">
                    <FaPhoneAlt className="text-[#A60000] text-sm" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">
                      {AboutUsData.aboutCompany.ctaSecondary.callText}
                    </p>
                    <p className="text-gray-900 font-semibold text-base">
                      {AboutUsData.aboutCompany.ctaSecondary.callNumber}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>
      {/* Why Choose US */}
      <WhyChooseUs {...AboutUsData.whyChooseUsData} />
      {/* MISSION SECTION */}
      <section className="bg-white py-24 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            transition={{ duration: 0.5 }}
            className="mb-16 text-left"
          >
            <p className="uppercase tracking-[3px] text-xs font-semibold text-[#A60000] mb-3">
              Purpose & Direction
            </p>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight max-w-4xl">
              {AboutUsData.missionVission.title}
            </h2>

            <div className="mt-5 w-16 h-[3px] bg-[#A60000] rounded-full"></div>
          </motion.div>

          {/* Mission */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative bg-gray-50 rounded-2xl p-8 sm:p-10 mb-14"
          >
            <span className="absolute left-0 top-8 w-1 h-16 bg-[#A60000] rounded-full"></span>

            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Our Mission
            </h3>

            <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-4xl">
              {AboutUsData.missionVission.missionText}
            </p>
          </motion.div>

          {/* Vision */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="relative bg-gray-50 rounded-2xl p-8 sm:p-10"
          >
            <span className="absolute left-0 top-8 w-1 h-16 bg-[#A60000] rounded-full"></span>

            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Our Vision
            </h3>

            <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-4xl">
              {AboutUsData.missionVission.visionText}
            </p>
          </motion.div>
        </div>
      </section>
      {/* VALUES SECTION */}
      <section className="bg-gray-50 py-24 px-5 sm:px-6">
      <div className="max-w-7xl mx-auto">

        {/* Section Header */}
        <div className="mb-16 max-w-3xl">
          <p className="uppercase tracking-[3px] text-xs font-semibold text-[#A60000] mb-3">
            Our Principles
          </p>

          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
            {AboutUsData.coreValues.title}
          </h2>

          <div className="mt-5 w-14 h-[3px] bg-[#A60000] rounded-full"></div>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {AboutUsData.coreValues.values.map((item: any, i: number) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="
                bg-white border border-gray-200 rounded-xl p-8
                hover:border-[#A60000]/40 hover:shadow-md
                transition-all
              "
            >
              {/* Icon */}
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gray-100 text-[#A60000] text-2xl">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.title}
                </h3>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
      {/* TEAM SECTION */}
        <section className="bg-gray-90 py-24 px-5 sm:px-6">
      <div className="max-w-7xl mx-auto">

        {/* Section Header */}
        <div className="mb-16 max-w-3xl">
          <p className="uppercase tracking-[3px] text-xs font-semibold text-[#A60000] mb-3">
            Leadership
          </p>

          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
            {AboutUsData.team.title}
          </h2>

          <div className="mt-5 w-14 h-[3px] bg-[#A60000] rounded-full"></div>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {AboutUsData.team.list.map((member: any, idx: number) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              className="
                bg-white rounded-xl border border-gray-200
                hover:border-gray-300 hover:shadow-lg
                transition-all
              "
            >
              {/* Image */}
              <div className="overflow-hidden rounded-t-xl">
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-full h-56 sm:h-60 object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {member.name}
                </h3>

                <p className="text-sm font-medium text-[#A60000] mt-1">
                  {member.role}
                </p>

                {/* Divider */}
                <div className="mt-4 mb-4 w-12 h-[2px] bg-gray-200 rounded"></div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed">
                  {member.desc || "Committed to operational excellence and strategic leadership."}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
    </div>
  );
};

export default AboutUs;
